import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  JobPostingDetailDto,
  JobPostingPaginatedListDto,
  CreateJobPostingRequestDto,
  UpdateJobPostingRequestDto,
  CloseJobPostingRequestDto,
  InterestedContractorDto,
  GetJobPostingsQueryParams
} from '../models/job-posting.model';

@Injectable({
  providedIn: 'root'
})
export class JobPostingService {
  private readonly baseUrl = '/api/job-postings';

  constructor(private http: HttpClient) {}

  /**
   * GET /api/job-postings/facility/{facilityId}
   * Get paginated list of job postings for the facility
   */
  getJobPostingsByFacility(
    facilityId: string,
    query?: GetJobPostingsQueryParams
  ): Observable<JobPostingPaginatedListDto> {
    let params = new HttpParams();

    if (query?.status) {
      params = params.set('status', query.status);
    }
    if (query?.pageNumber) {
      params = params.set('pageNumber', query.pageNumber.toString());
    }
    if (query?.pageSize) {
      params = params.set('pageSize', query.pageSize.toString());
    }

    return this.http.get<JobPostingPaginatedListDto>(
      `${this.baseUrl}/facility/${facilityId}`,
      { params }
    );
  }

  /**
   * GET /api/job-postings/{id}/detail
   * Get complete details of a job posting (facility admin view)
   * Returns all lifecycle stages (draft, posted, closed)
   * Includes: interestedCount, description, closedAt, closureOutcome
   */
  getJobPostingDetail(id: string): Observable<JobPostingDetailDto> {
    return this.http.get<JobPostingDetailDto>(`${this.baseUrl}/${id}/detail`);
  }

  /**
   * POST /api/job-postings
   * Create a new job posting (can be draft or published immediately)
   */
  createJobPosting(
    request: CreateJobPostingRequestDto
  ): Observable<JobPostingDetailDto> {
    return this.http.post<JobPostingDetailDto>(this.baseUrl, request);
  }

  /**
   * PUT /api/job-postings/{id}
   * Edit an existing job posting
   * Null/undefined fields are ignored (left as is)
   */
  updateJobPosting(
    id: string,
    request: UpdateJobPostingRequestDto
  ): Observable<JobPostingDetailDto> {
    return this.http.put<JobPostingDetailDto>(`${this.baseUrl}/${id}`, request);
  }

  /**
   * POST /api/job-postings/{id}/publish
   * Publish a draft job posting (Draft → Posted)
   */
  publishJobPosting(id: string): Observable<JobPostingDetailDto> {
    return this.http.post<JobPostingDetailDto>(
      `${this.baseUrl}/${id}/publish`,
      {}
    );
  }

  /**
   * POST /api/job-postings/{id}/close
   * Close a job posting with outcome (Posted → Closed)
   */
  closeJobPosting(
    id: string,
    request: CloseJobPostingRequestDto
  ): Observable<JobPostingDetailDto> {
    return this.http.post<JobPostingDetailDto>(
      `${this.baseUrl}/${id}/close`,
      request
    );
  }

  /**
   * GET /api/job-postings/{id}/interested-contractors
   * Get list of contractors interested in this job posting
   */
  getInterestedContractors(
    jobPostingId: string
  ): Observable<InterestedContractorDto[]> {
    return this.http.get<InterestedContractorDto[]>(
      `${this.baseUrl}/${jobPostingId}/interested-contractors`
    );
  }

  /**
   * PUT /api/job-postings/{jobPostingId}/interested-contractors/{contractorId}/like
   * Like or unlike a contractor for this job posting
   * Returns the updated InterestedContractorDto with isLiked field updated
   */
  toggleLikeContractor(
    jobPostingId: string,
    contractorId: string,
    isLiked: boolean
  ): Observable<InterestedContractorDto> {
    return this.http.put<InterestedContractorDto>(
      `${this.baseUrl}/${jobPostingId}/interested-contractors/${contractorId}/like`,
      { isLiked }
    );
  }
}
